// Catalog.ID Client — leaving a registration wizard
// ============================================================================
// Before the account exists, the footer's Cancel throws the registration away:
// the onboarding stash holds throwaway keys and at most a payment leg, and
// nothing on the node depends on it. From the step that CREATES the account on
// — personal and organization: Completion; delegate activation: Activate —
// there is no Back, and throwing the stash away would leave an account nobody
// can finish: its throwaway password lived only in the stash, only the wizard's
// print gate marks onboarding complete, and the node purges an unfinished
// account after 7 days (OnboardingPurgeService) with the code that paid for it
// spent — printed credentials or not (staging test 2026-10-15).
//
// So from that step on the button says "Finish later" and keeps the stash; the
// login screen's "Resume registration" brings the member back to the step they
// left, and its Discard says what discarding costs before it does it. The
// behaviour is read off the label, so the two cannot disagree.

import type { WizardStep } from '@catalog-shell';

export const FINISH_LATER_LABEL = 'Finish later';

/**
 * Label `firstStepId` and every step after it "Finish later". The step objects
 * are labelled in place, not copied: steps that redraw themselves do it through
 * `this`. Throws on an unknown id — a renamed step must not quietly turn every
 * exit back into a discard.
 */
export function finishLaterFrom(steps: WizardStep[], firstStepId: string): WizardStep[] {
    const from = steps.findIndex(s => s.id === firstStepId);
    if (from === -1) throw new Error(`finishLaterFrom: no step "${firstStepId}"`);
    for (let i = from; i < steps.length; i++) steps[i].cancelLabel = FINISH_LATER_LABEL;
    return steps;
}

/** Does leaving at step `index` keep the registration (a "Finish later" step)? */
export function keepsRegistrationOnExit(steps: readonly WizardStep[], index: number): boolean {
    return steps[index]?.cancelLabel === FINISH_LATER_LABEL;
}
